import { useCallback } from 'react';
import { getIn, useFormikContext } from 'formik';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';

import { ensureDefined } from '@e-socna/utils';
import type { EMPTY_STRING } from '@e-socna/empty';
import type { UnitId } from '@e-socna/data-layer';

import { getTouchedError } from '../../../../utils/get-touched-error.util';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useContextualizedFields } from '../../../../hooks/useContextualizedFields.hook';
import { QUANTITY_VALUE_FIELD_STEP } from '../../../../constants/number-field-steps.constant';
import { IdSelect } from '../../../../components/IdSelect';
import type { MaterialListCmp_Material } from '../../../../components/MaterialList/hooks/useFindAllMaterialsQuery.hook';
import type { IdOption } from '../../../../components/IdSelect';

import type {
  MaterialUsageForm_MaterialUsage,
  OptionalMaterialUsageFormContext,
  RequiredMaterialUsageFormContext,
} from '../../fragments/material-usage.fragment';

interface Props {
  readonly allMaterials: readonly MaterialListCmp_Material[];
  readonly materialUsage: MaterialUsageForm_MaterialUsage;
  readonly formContext?: string;
  readonly index: number;
}

export const Item = ({
  allMaterials,
  materialUsage,
  formContext,
  index,
}: Readonly<Props>) => {
  const { units } = useReferencesContext();

  const { handleBlur, handleChange, errors, setFieldValue, touched } =
    useFormikContext<
      OptionalMaterialUsageFormContext | RequiredMaterialUsageFormContext
    >();

  const { material } = ensureDefined({
    material: allMaterials.find(
      (material) => material._id === materialUsage.materialId,
    ),
  });

  const indexAsString = index.toFixed();

  const fieldsBase = useContextualizedFields<
    Readonly<{
      quantity: MaterialUsageForm_MaterialUsage['quantity'];
    }>
  >(
    {
      quantity: {
        id: `material-usage-${indexAsString}-quantity`,
        name: `materialUsages[${indexAsString}].quantity`,
      },
    },
    formContext,
  );

  const fields = {
    ...fieldsBase,
    quantityValue: {
      id: `${fieldsBase.quantity.id}-value`,
      name: `${fieldsBase.quantity.name}.value`,
      value: fieldsBase.quantity.value.value,
    },
    quantityUnitId: {
      id: `${fieldsBase.quantity.id}-unitId`,
      name: `${fieldsBase.quantity.name}.unitId`,
      value: fieldsBase.quantity.value.unitId,
    },
  };

  const handleUnitIdChange = useCallback(
    (id: UnitId | typeof EMPTY_STRING) => {
      void setFieldValue(fields.quantityUnitId.name, id);
    },
    [fields.quantityUnitId.name, setFieldValue],
  );

  return (
    <Paper key={material._id} sx={{ width: '100%', p: 2 }}>
      <Typography sx={{ mb: 4 }} variant="h6">
        {material.code} - {material.label}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ sm: 6, xs: 12 }}>
          <TextField
            error={
              !!(
                getIn(errors, fields.quantityValue.name) &&
                getIn(touched, fields.quantityValue.name)
              )
            }
            fullWidth
            helperText={getTouchedError(
              errors,
              touched,
              fields.quantityValue.name,
            )}
            id={fields.quantityValue.id}
            label="Quantité"
            name={fields.quantityValue.name}
            onBlur={handleBlur}
            onChange={handleChange}
            required
            slotProps={{
              htmlInput: {
                min: QUANTITY_VALUE_FIELD_STEP,
                step: QUANTITY_VALUE_FIELD_STEP,
              },
            }}
            type="number"
            value={fields.quantityValue.value}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }}>
          <IdSelect<UnitId, IdOption<UnitId>>
            disabled
            error={
              !!(
                getIn(errors, fields.quantityUnitId.name) &&
                getIn(touched, fields.quantityUnitId.name)
              )
            }
            helperText={
              !!(
                getIn(errors, fields.quantityUnitId.name) &&
                getIn(touched, fields.quantityUnitId.name)
              ) && (getIn(errors, fields.quantityUnitId.name) as string)
            }
            id={fields.quantityUnitId.id}
            label="Unité"
            name={fields.quantityUnitId.name}
            onBlur={handleBlur}
            onChange={handleUnitIdChange}
            options={units.map((unit) => ({
              id: unit._id,
              label: unit.label,
            }))}
            required
            value={fields.quantityUnitId.value as UnitId}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
